import { CampaignStateResponsePayload } from '../types/CampaignTypes';
import { logError, logWarning } from '../telemetry';
import { ProgrammableSurfacesEvent, ProgrammableSurfacesEvents, ProgrammableSurfacesTransport, ProgrammableSurfacesEventWithoutPayload, ProgrammableSurfacesEventWithPayload } from './types';

declare let OfficeFirstParty: any

/**
 * Event handler function type for ProgrammableSurfaces events.
 * Handlers receive the event payload and process it.
 */
type EventHandler<TPayload = unknown> = (payload: TPayload) => void;

export class ProgrammableSurfacesCommunication<TEventType extends ProgrammableSurfacesEvents, TPayload = unknown> implements ProgrammableSurfacesTransport<TEventType, TPayload> {
    private static instance: ProgrammableSurfacesCommunication<ProgrammableSurfacesEvents, unknown> | null = null;

    /**
     * Event handlers registry. Maps event types to sets of handler functions.
     * Using Set to avoid duplicate handlers and enable efficient removal.
     */
    private eventHandlers: Map<ProgrammableSurfacesEvents, Set<EventHandler<unknown>>> = new Map();

    // Promise to handle registration of the communication channel.
    // This is used to ensure the communication channel is set up before sending events.
    // It can be useful for testing or when the channel setup is asynchronous.
    // Note: This is not currently used in the implementation, but can be extended in the future.
    private registrationPromise: any

    private constructor() {
        // Set up inbound event listener from Win32 via OfficeFirstParty.PrivateApi
        this.setupInboundEventListener();
    }

    static getInstance(): ProgrammableSurfacesCommunication<ProgrammableSurfacesEvents, unknown> {
        if (!ProgrammableSurfacesCommunication.instance) {
            ProgrammableSurfacesCommunication.instance = new ProgrammableSurfacesCommunication<ProgrammableSurfacesEvents, unknown>();
        }
        return ProgrammableSurfacesCommunication.instance as ProgrammableSurfacesCommunication<ProgrammableSurfacesEvents, unknown>;
    }

    /**
     * Sends an event to Win32 client.
     * Wrapped in try-catch for graceful error handling.
     *
     * @param event - Event to send with type and payload
     */
    sendEvent(event: ProgrammableSurfacesEvent<TEventType, TPayload>): void {
        try {
            console.log('[ProgrammableSurfacesCommunication] Sending event:', event);
            if (this.registrationPromise) {
                this.registrationPromise.then(() => {
                    OfficeFirstParty.PrivateApi.execute(JSON.stringify(event));
                });
            }
            else {
                logError('CommunicationSendError', `PrivateApi registration promise is null. Cannot send event: ${event.eventType}`);
            }
        } catch (error) {
            logError('CommunicationSendError', `Send failed: ${error}`);
        }
    }

    /**
     * Registers an event handler for a specific event type.
     * Multiple handlers can be registered for the same event type.
     *
     * @param eventType - The event type to listen for
     * @param handler - Callback function to invoke when event is received
     */
    addEventListener<T = unknown>(
        eventType: ProgrammableSurfacesEvents,
        handler: EventHandler<T>
    ): void {
        if (!this.eventHandlers.has(eventType)) {
            this.eventHandlers.set(eventType, new Set());
        }

        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            handlers.add(handler as EventHandler<unknown>);
        }
    }

    /**
     * Removes a previously registered event handler.
     * Enables cleanup to prevent memory leaks in long-running sessions.
     *
     * @param eventType - The event type the handler was registered for
     * @param handler - The handler function to remove
     */
    removeEventListener<T = unknown>(
        eventType: ProgrammableSurfacesEvents,
        handler: EventHandler<T>
    ): void {
        const handlers = this.eventHandlers.get(eventType);
        if (handlers) {
            handlers.delete(handler as EventHandler<unknown>);
        }
    }

    /**
     * Sets up the inbound message listener from Win32.
     * Uses OfficeFirstParty.PrivateApi.onPrivateEvent to establish bidirectional communication.
     * This is the standard pattern for Win32↔Web communication in SDX.
     */
    private setupInboundEventListener(): void {
        try {
            // Register with OfficeFirstParty.PrivateApi.onPrivateEvent for bidirectional communication
            if (!OfficeFirstParty?.PrivateApi?.onPrivateEvent) {
                console.warn('[ProgrammableSurfacesCommunication] OfficeFirstParty.PrivateApi.onPrivateEvent not available - inbound events disabled');
                return;
            }

            this.registrationPromise = OfficeFirstParty.PrivateApi.onPrivateEvent(this.onMessage.bind(this));
        } catch (error) {
            logError('CommunicationSetupError', `Error setting up inbound listener: ${error}`);
        }
    }

    /**
     * Handles inbound messages from Win32 client.
     * Parses JSON, validates event structure, and dispatches to registered handlers.
     *
     * @param arg - Message event from PrivateApi.onPrivateEvent
     */
    private onMessage(arg: unknown): void {
        let messageData: ProgrammableSurfacesEvent<ProgrammableSurfacesEvents, CampaignStateResponsePayload> | undefined;

        try {
            // Parse message data (may be JSON string or already parsed object)
            messageData = typeof arg === 'string' ? JSON.parse(arg) : arg as ProgrammableSurfacesEvent<ProgrammableSurfacesEvents, CampaignStateResponsePayload>;
        } catch (error) {
            logError('CommunicationParseError', `Parse failed: ${error}`);
            return;
        }

        // Validate event structure
        if (!this.isValidEvent(messageData)) {
            logWarning('CommunicationInvalidEventStructure', `Invalid event structure received`);
            return;
        }

        // if payload is empty return early
        if (!messageData.payload || Object.keys(messageData.payload).length === 0) {
            logWarning('CommunicationEmptyPayload', `Empty payload for event: ${messageData.eventType}`);
            return;
        }

        // Normalize payload for CampaignStateResponse (data comes as stringified JSON from Win32)
        if (messageData.eventType === ProgrammableSurfacesEvents.CampaignStateResponse && messageData.payload) {
            messageData.payload = this.normalizeCampaignStateResponsePayload(messageData.payload) as CampaignStateResponsePayload;
        }

        // Dispatch to registered handlers
        const eventType = messageData.eventType;
        const handlers = this.eventHandlers.get(eventType);

        if (handlers && handlers.size > 0) {
            handlers.forEach(handler => {
                try {
                    handler(messageData!.payload);
                } catch (error) {
                    logError('CommunicationHandlerError', `Handler error for ${ProgrammableSurfacesEvents[eventType]}: ${error}`);
                }
            });
        }
    }

    /**
     * Type guard: Validates event structure.
     * Ensures event has eventType property. Payload is optional.
     *
     * @param obj - Object to validate
     * @returns true if obj is a valid ProgrammableSurfacesEvent
     */
    private isValidEvent(obj: unknown): obj is ProgrammableSurfacesEvent<ProgrammableSurfacesEvents, unknown> {
        if (!obj || typeof obj !== 'object') {
            return false;
        }

        const event = obj as ProgrammableSurfacesEvent<ProgrammableSurfacesEvents, unknown>;
        return (
            typeof event.eventType === 'string' &&
            event.eventType in ProgrammableSurfacesEvents
        );
    }

    /**
     * Normalizes CampaignStateResponse payload by parsing stringified data field.
     * Win32 sends payload.data as a JSON string that needs parsing.
     *
     * @param payload - Raw payload from Win32
     * @returns Normalized payload with parsed data field
     */
    private normalizeCampaignStateResponsePayload(payload: unknown): CampaignStateResponsePayload | unknown {
        if (payload && typeof payload === 'object' && 'data' in payload) {
            const payloadWithData = payload as { data: unknown };
            if (typeof payloadWithData.data === 'string') {
                try {
                    payloadWithData.data = JSON.parse(payloadWithData.data);
                } catch (error) {
                    logWarning('CommunicationParseError', `Failed to parse stringified data field: ${error}`);
                }
            }
        }
        return payload;
    }
} 